import React, { useMemo, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import * as yup from 'yup';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  useEditUser,
  useDeactivateUser,
  useGetSettingsUsers,
  useDeleteUser,
} from 'new/modules/OfficeSettings/hooks';
import Table, { Column } from 'new/components/Table/Table';
import { useGetUserFromToken } from 'new/modules/MembershipPlan/hooks';
import {
  AddUser,
  SettingsUser,
  EditUserRequest,
  PermissionType,
} from 'new/modules/OfficeSettings/types';
import { isAdmin } from 'new/utils/permissions';
import { Form } from 'new/components/Form';
import DataView from 'new/components/DataView';
import IconFontAwesome from 'new/components/IconFontAwesome';
import TableActionButton from 'new/components/Table/TableActionButton';
import useToggle from 'new/hooks/useToggle';
import ConfirmModal from 'new/components/ConfirmModal';
import { PHONE_NUMBER_MASK } from 'new/constants/mask';
import LinkButton from 'new/components/LinkButton';
import EditUserModal from './EditUserModal';
import {
  emailSchema,
  phoneNumberSchema,
  stringRequiredSchema,
} from 'new/constants/schema';
import PermissionsModal from 'new/modules/OfficeSettings/components/PermissionsModal';
import useActiveOffice from 'new/hooks/useActiveOffice';
import { checkExistingEmail } from 'new/modules/InternalPatientSignup/endpoints';
import { normalizePhoneNumber } from 'new/utils/strings';

const UsersTableWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),

  '.businessOwnerTable': {
    marginBottom: theme.spacing(2),

    '.MuiTableRow-root': {
      height: 50,
    },
    thead: {
      display: 'none',
    },
  },

  table: {
    minHeight: 'fit-content',

    'td:first-of-type': {
      display: 'flex',
      justifyContent: 'center',
    },
  },

  td: {
    color: `${theme.palette.primary.dark} !important`,
  },

  '.MuiTableRow-root': {
    height: 40,
    display: 'flex',
    alignItems: 'center',
    borderRadius: 5,

    ':nth-child(even)': {
      background: '#fff !important',
    },
  },

  '.planCoordinator': {
    padding: '0 2px',
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    fontWeight: 'bold',
    width: 'fit-content',
  },

  '.actionsBox': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 5,
  },

  thead: {
    th: {
      height: 50,
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

const AddUserTableWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),

  svg: {
    transform: 'rotate(45deg)',
    margin: '22px 0 0 15px',
    color: theme.palette.grey[600],
  },

  p: {
    fontWeight: 700,
  },

  'table, tr, tr.MuiTableRow-root:hover': {
    cursor: 'auto !important',
    backgroundColor: '#fff !important',
  },

  table: {
    '.MuiTableRow-root, .MuiFormControl-root': {
      height: 70,
      marginTop: theme.spacing(2),
    },
  },

  thead: {
    display: 'none',
  },

  td: {
    paddingTop: 0,
  },

  'tr.MuiTableRow-root:hover': {
    border: '1px solid transparent',
  },

  button: {
    marginTop: theme.spacing(2),
    color: theme.palette.grey[600],
  },
}));

const OfficeSettingsTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  fontWeight: 700,
  color: theme.palette.primary.dark,
}));

export const permissionsLabelMap: Record<PermissionType, string> = {
  dentist: 'Super Admin',
  dentist_standard: 'Standard',
  office_admin: 'Office Admin',
};

const initialAddUserValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

const addUserSchema = yup.object({
  firstName: stringRequiredSchema,
  lastName: stringRequiredSchema,
  email: emailSchema
    .required()
    .test(
      'is email in use',
      'This Email already exists in our system',
      async (email) => {
        if (email) {
          try {
            const response = await checkExistingEmail.endpoint({ email });

            return !response.data.error;
          } catch (error) {
            return true;
          }
        } else {
          return false;
        }
      },
    ),
  phone: phoneNumberSchema,
});

const columnsCommon: Column<SettingsUser>[] = [
  {
    id: 'firstName',
    Header: 'First Name',
    accessor: (row) => (
      <Box width="100%" textAlign="left">
        {row.user.firstName}
      </Box>
    ),
    width: 200,
  },
  {
    id: 'lastName',
    Header: 'Last Name',
    accessor: (row) => row.user.lastName,
    width: 200,
  },
  {
    id: 'email',
    Header: 'Email',
    accessor: (row) => row.user.email,
    width: 300,
    maxWidth: 400,
    disableSortBy: true,
  },
  {
    id: 'phone',
    Header: 'Phone',
    accessor: (row) => normalizePhoneNumber(row.user.phoneNumber?.number),
    width: 200,
    disableSortBy: true,
  },
  {
    id: 'permissions',
    Header: 'Permissions',
    width: 90,
    accessor: (row) => {
      if (row.isOwner) {
        return 'Business Owner';
      }

      return permissionsLabelMap[row.user.type] || row.user.type;
    },
  },
];

const columnsOwner = [
  {
    id: 'status',
    Header: '',
    accessor: () => <IconFontAwesome name="faLock" />,
    width: 70,
    disableSortBy: true,
  },
  ...columnsCommon,
  {
    id: '_empty',
    Header: '',
    width: 170,
    disableSortBy: true,
  },
];

type DentistInfoIdAndUserId = {
  dentistInfoId: number;
  userId: number;
};

const FORM_ADD_USER_ID = 'addUserForm';

const OfficeSettingsUsers: React.FC = () => {
  const { activeOfficeId } = useActiveOffice();
  const {
    data = [],
    isLoading,
    refetch,
    isRefetching,
  } = useGetSettingsUsers(activeOfficeId);
  const { data: user } = useGetUserFromToken();
  const formRef = useRef<FormikProps<typeof initialAddUserValues>>(null);
  const statusFormRef = useRef<FormikProps<{ status: boolean[] }>>(null);

  const [permissionModalOpen, togglePermissionsModalOpen] = useToggle();

  const { mutate: editUser, isLoading: isSubmittingEditUser } = useEditUser();
  const { mutate: deleteUser, isLoading: isSubmittingDeleteUser } =
    useDeleteUser();
  const { mutate: deactivateUser, isLoading: isDeactivatingUser } =
    useDeactivateUser();

  const [deletedUserInfo, setDeletedUserInfo] =
    useState<DentistInfoIdAndUserId>();
  const [editedUser, setEditedUser] = useState<EditUserRequest>();
  const [userPermissionsId, setUserPermissionsId] = useState<number>();

  const shouldDisplayActions = isAdmin(user?.data.type);

  const columnsAddUser: Column<AddUser>[] = useMemo(
    () => [
      {
        id: 'emptyCol1',
        Header: '',
        accessor: () => <IconFontAwesome name="faXmark" />,
        width: 70,
      },
      {
        id: 'firstName',
        accessor: () => (
          <Form.Input name="firstName" label="First Name" fullWidth />
        ),
        width: 200,
      },
      {
        id: 'lastName',
        accessor: () => (
          <Form.Input name="lastName" label="Last Name" fullWidth />
        ),
        width: 200,
      },
      {
        id: 'email',
        accessor: () => <Form.Input name="email" label="Email" fullWidth />,
        width: 300,
        maxWidth: 400,
      },
      {
        id: 'phone',
        accessor: () => (
          <Form.InputWithMask
            name="phone"
            label="Phone"
            mask={PHONE_NUMBER_MASK}
            guide
            fullWidth
            placeholder="XXX-XXX-XXXX"
          />
        ),
        width: 200,
      },
      {
        id: 'permissions',
        accessor: () => (
          <LinkButton type="submit" form={FORM_ADD_USER_ID}>
            + Set Permissions
          </LinkButton>
        ),
        width: 90,
      },
      {
        id: 'emptyCol2',
        width: 170,
      },
    ],
    [],
  );

  const submitEdit = (values: AddUser) => {
    if (editedUser) {
      editUser(
        {
          dentistInfoId: editedUser.dentistInfoId,
          userId: editedUser.userId,
          type: editedUser.type,
          ...values,
        },
        {
          onSuccess: () => {
            setEditedUser(undefined);
            refetch();
          },
        },
      );
    }
  };

  const submitDelete = () => {
    if (deletedUserInfo) {
      deleteUser(
        {
          dentistInfoId: deletedUserInfo.dentistInfoId,
          userId: deletedUserInfo.userId,
        },
        {
          onSuccess: () => {
            setEditedUser(undefined);
            refetch();
          },
        },
      );
    }
  };

  const toggleColumn: Column<SettingsUser> = {
    id: 'status',
    Header: '',
    accessor: (row, index) => {
      let color: 'success' | 'warning' = 'success';

      if (!row.user.hasHash) {
        color = 'warning';
      }

      if (
        row.user?.id != user?.data.id &&
        row.isOwner &&
        row?.userId !== Number(activeOfficeId)
      )
        return (
          <Form.Switch
            name={`status.${index}`}
            color={color}
            onChange={(_, checked) => {
              if (user?.data) {
                deactivateUser(
                  {
                    clientId: row.userId,
                    isDeleted: !checked,
                    dentistInfoId: row.dentistInfoId,
                    user: user.data,
                    userId: row.userId,
                  },
                  {
                    onSuccess: (data) => {
                      if (data.error?.errors) {
                        statusFormRef?.current?.setFieldValue(
                          `status.${index}`,
                          !checked,
                        );
                      }
                    },
                  },
                );
              }
            }}
            disabled={isDeactivatingUser}
          />
        );
    },
    width: 70,
    disableSortBy: true,
  };

  const columns: Column<SettingsUser>[] = useMemo(
    () => [
      ...(shouldDisplayActions ? [toggleColumn] : []),
      ...columnsCommon,
      {
        id: 'planCoordinator',
        Header: '',
        accessor: (row) =>
          row.isFinanceCoordinator && <Box className="planCoordinator">PC</Box>,
        width: 70,
        disableSortBy: true,
      },
      {
        id: 'actions',
        accessor: (row) =>
          shouldDisplayActions && (
            <Box className="actionsBox">
              <TableActionButton
                name="faPen"
                disabled={row.isOwner && row.userId !== Number(activeOfficeId)}
                onClick={() =>
                  setEditedUser({
                    dentistInfoId: row.dentistInfoId,
                    userId: row.userId,
                    firstName: row.user.firstName,
                    lastName: row.user.lastName,
                    email: row.user.email,
                    phone: row.user.phoneNumber?.number,
                    type: row.user.type,
                  })
                }
              />
              <TableActionButton
                name="faShieldHalved"
                disabled={row.isOwner && row.userId !== Number(activeOfficeId)}
                onClick={() => {
                  togglePermissionsModalOpen();
                  setUserPermissionsId(row.userId);
                }}
              />
              <TableActionButton
                name="faTrashCan"
                disabled={row.isOwner && row.userId !== Number(activeOfficeId)}
                onClick={() =>
                  setDeletedUserInfo({
                    dentistInfoId: row.dentistInfo.id,
                    userId: row.userId,
                  })
                }
              />
            </Box>
          ),
        width: 100,
        disableSortBy: true,
      },
    ],
    [isDeactivatingUser, user?.data],
  );

  return (
    <DataView data={data} isLoading={isLoading}>
      {(data) => {
        const initialValues = {
          status: data.map((row) => !row.user.isDeleted),
        };
        const dataOwner = data.filter((user) => user.isOwner);
        const dataWithoutOwner = data.filter((user) => !user.isOwner);

        return (
          <>
            <UsersTableWrapper>
              {!!dataOwner.length && (
                <>
                  <OfficeSettingsTitle>Business Owner</OfficeSettingsTitle>
                  <Table
                    className="businessOwnerTable"
                    data={dataOwner}
                    columns={columnsOwner}
                    isLoading={isRefetching}
                  />
                </>
              )}
              <AddUserTableWrapper>
                <OfficeSettingsTitle>Additional Users</OfficeSettingsTitle>
                <Form
                  id={FORM_ADD_USER_ID}
                  formikPropsRef={formRef}
                  initialValues={initialAddUserValues}
                  validationSchema={addUserSchema}
                  onSubmit={togglePermissionsModalOpen}
                  validateOnChange={false}
                  validateOnBlur={false}
                >
                  <Table
                    className="addUserTable"
                    data={[initialAddUserValues]}
                    columns={columnsAddUser}
                  />
                </Form>
              </AddUserTableWrapper>
              <Form
                formikPropsRef={statusFormRef}
                initialValues={initialValues}
                enableReinitialize
              >
                <Table
                  data={dataWithoutOwner}
                  columns={columns}
                  isLoading={isRefetching}
                  height={500}
                />
              </Form>
            </UsersTableWrapper>
            <EditUserModal
              editedUser={editedUser}
              isLoading={isSubmittingEditUser}
              onClose={() => setEditedUser(undefined)}
              onSubmit={submitEdit}
            />
            <ConfirmModal
              modalTitle="Delete User?"
              modalText="You'll permanently lose this user's information."
              confirmButtonLabel="DELETE USER"
              open={!!deletedUserInfo}
              isLoading={isSubmittingDeleteUser}
              onDelete={submitDelete}
              onClose={() => setDeletedUserInfo(undefined)}
            />
            <PermissionsModal
              open={permissionModalOpen}
              onClose={() => {
                togglePermissionsModalOpen();
                setUserPermissionsId(undefined);
              }}
              activeUser={data.find(
                (user) => user.userId === userPermissionsId,
              )}
              newUser={formRef.current ? formRef.current.values : undefined}
              refetch={refetch}
              resetAddForm={formRef.current?.resetForm}
              users={data}
            />
          </>
        );
      }}
    </DataView>
  );
};

export default OfficeSettingsUsers;
